import API from "../config/api.js";
import AppLayout from "../layouts/AppLayout.jsx";

const FEATURES_MONTHLY = [
  { text: "Monthly content calendar",            included: true  },
  { text: "Done-for-you posts, Reels and captions", included: true },
  { text: "3,000+ travel photos and videos",     included: true  },
  { text: "Full AI Marketing Assistant",         included: true  },
  { text: "Unlimited scheduling",                included: true  },
  { text: "60 day templates archive",            included: true  },
  { text: "Email marketing suite",               included: true  },
  { text: "Personalized social media audit",     included: false },
];

const FEATURES_ANNUAL = [
  { text: "Monthly content calendar",            included: true },
  { text: "Done-for-you posts, Reels and captions", included: true },
  { text: "3,000+ travel photos and videos",     included: true },
  { text: "Full AI Marketing Assistant",         included: true },
  { text: "Unlimited scheduling",                included: true },
  { text: "90 day templates archive",            included: true },
  { text: "Email marketing suite",               included: true },
  { text: "Personalized social media audit",     included: true },
];

const FAQ = [
  {
    q: "What's included in the membership?",
    a: "Your subscription includes our new social media scheduler, your own unique calendar with 30 new posts per month, 5 new Canva templates, 10 new Reels, a monthly marketing plan, access to training and more!",
  },
  {
    q: "Does the membership have a term commitment?",
    a: "We understand that even just $49 is a big decision. That's why there's ZERO RISK. You're not locked into a specific term, and you can pause or cancel your membership at any time you decide it's no longer serving you and your business. There are no refunds granted for monthly or annual membership payments after they have been processed.",
  },
  {
    q: "How does billing work?",
    a: "When you join Travel Agent Collective, you are charged on the day you sign up and receive access to the current month's content. Moving forward, you will be charged on the same day every single month.",
  },
  {
    q: "How much is the TAC membership?",
    a: "After you've tried TAC for free, the monthly membership is $49/month and the annual membership is $597. Each is billed based on your signup date.",
  },
  {
    q: "Can I start with monthly and switch to annual?",
    a: "Absolutely! If you join as a monthly member and like what you see, you can upgrade to the annual membership of $597 at any time to access bonus content. Memberships are pro-rated, so the $57 monthly membership fee will be deducted from your annual fee.",
  },
  {
    q: "Will this work for my travel niche?",
    a: "Yes! All of the monthly writing prompts can be customized to fit any travel business and any niche. Canva templates can also be customized to fit your brand and your business.",
  },
];

function FeatureItem({ text, included }) {
  return (
    <li className="d-flex align-items-start gap-2 mb-3">
      <span style={{ color: included ? "var(--primary-color)" : "#ccc", flexShrink: 0, marginTop: 2 }}>
        {included ? <i className="fi fi-rr-check" /> : <i className="fi fi-rr-cross-small" />}
      </span>
      <span style={{ color: included ? "inherit" : "#aaa" }}>{text}</span>
    </li>
  );
}

function PlanCard({ badge, badgeBg = "bg-primary", title, price, period, checkoutUrl, features }) {
  return (
    <div className="col-md-4">
      <div className="position-relative">
        {/* Badge */}
        {badge && (
          <div className="text-center position-absolute w-100" style={{ top: -8, zIndex: 2 }}>
            <span className={`${badgeBg} text-white rounded-pill px-4 py-2 fw-bold small`}>
              {badge}
            </span>
          </div>
        )}

        <div
          className="card h-100 p-4 rounded-4"
          style={{
            border: badge === "Most Popular" ? "2px solid var(--primary-color)" : "1px solid #e0e0e0",
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          }}
        >
          {/* Price */}
          <div className="mt-3 mb-4">
            <h2 className="fw-bold fs-4 mb-1">{title}</h2>
            <div className="d-flex align-items-end gap-1">
              <span className="fw-bold" style={{ fontSize: "3.5rem", lineHeight: 1 }}>${price}</span>
              <span className="text-muted mb-2">{period}</span>
            </div>
          </div>

          {/* CTA */}
          <a href={checkoutUrl} className="btn btn-primary w-100 rounded-pill py-3 mb-4 fw-bold fs-6">
            Get Started
          </a>

          {/* Features */}
          <ul className="list-unstyled mb-0">
            {features.map((f, i) => (
              <FeatureItem key={i} text={f.text} included={f.included} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function Plans() {
  // TODO: replace with real checkout URLs
  const monthlyUrl = API.baseUrl + "/checkout-stripe?idplan=a87ff679a2f3e71d9181a67b7542122c";
  const annualUrl  = API.baseUrl + "/checkout-stripe?idplan=c81e728d9d4c2f636f067f89cc14862c";

  return (
    <AppLayout currentPage="plans">
      <div className="container py-5">

        {/* Header */}
        <div className="text-center mb-5">
          <h5 className="text-primary fw-bold text-uppercase">Pricing</h5>
          <h1 className="fs-1 mt-2 fw-700">Find your perfect plan</h1>
          <p className="text-muted">
            Whether you're a seasoned pro, or just starting out — Travel Agent Collective has the resources for you!
          </p>
        </div>

        {/* Feature icons */}
        <div className="row justify-content-center mb-5">
          <div className="col-md-10 col-lg-8">
            <div className="row g-4 text-center">
              {[
                { icon: "fi-rr-calendar-day", label: "Custom Calendar" },
                { icon: "fi-rr-bolt",          label: "Auto Posting" },
                { icon: "fi-rr-circle-book-open",     label: "Content Library" },
                { icon: "fi-rr-browser",       label: "Visual Content Planner" },
                { icon: "fi-rr-play",          label: "One-Click Reels Scheduling" },
                { icon: "fi-rr-hastag",        label: "Hashtag Suggestions" },
              ].map(({ icon, label }) => (
                <div key={label} className="col-6 col-md-2">
                  <i className={`fi ${icon} fs-1 text-primary`} />
                  <div className="small mt-2">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Plan cards */}
        <div className="row justify-content-center g-5 pt-3">
          <PlanCard
            badge="Most Popular"
            badgeBg="bg-primary"
            title="Monthly"
            price="49"
            period="/mo"
            checkoutUrl={monthlyUrl}
            features={FEATURES_MONTHLY}
          />
          <PlanCard
            badge="Save 12%"
            badgeBg="bg-primary"
            title="Annual"
            price="50"
            period="/mo"
            checkoutUrl={annualUrl}
            features={FEATURES_ANNUAL}
          />
        </div>

        {/* FAQ */}
        <h2 className="mt-5 pt-5 fw-700">Frequently Asked Questions</h2>
        <p className="text-muted">
          Everything you need to know about the product and billing. Can't find the answer you're looking for? Please chat to our friendly team.
        </p>

        <div className="row g-4 mt-3">
          {FAQ.map(({ q, a }) => (
            <div key={q} className="col-md-4">
              <h6 className="fw-bold">{q}</h6>
              <p className="text-muted mb-0">{a}</p>
            </div>
          ))}
        </div>

        {/* Still have questions */}
        <div className="bg-white p-4 mt-5 rounded-4 border">
          <div className="row g-4 align-items-center">
            <div className="col-md-8">
              <h6 className="fw-bold">Still have questions?</h6>
              <p className="mb-0 text-muted">Can't find the answer you're looking for? Please chat to our friendly team.</p>
            </div>
            <div className="col-md-4 text-end">
              <a href="https://help.travelagentcollective.com/" className="btn btn-primary" target="_blank" rel="noreferrer">
                Get in touch
              </a>
            </div>
          </div>
        </div>

      </div>
    </AppLayout>
  );
}